import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface AssetAllocation {
  name: string;
  value: number;
  percentage: number;
  [key: string]: string | number;
}

interface AssetAllocationChartProps {
  data: AssetAllocation[];
  formatCurrency: (amount: number) => string;
}

// Asset allocation colors: Equity (blue), Fixed Income (purple), Cash (pink), Other (orange)
const COLORS = ['rgb(37, 99, 235)', 'rgb(124, 58, 237)', 'rgb(219, 39, 119)', 'rgb(251, 146, 60)', '#ef4444'];

// Compute display percentages so they always sum to exactly 100.00%
// All items are rounded to 2dp; the last item absorbs the rounding remainder.
function computeDisplayPercentages(data: AssetAllocation[]): string[] {
  if (data.length === 0) return [];
  const rounded = data.map(a => parseFloat(a.percentage.toFixed(2)));
  const sumExceptLast = rounded.slice(0, -1).reduce((s, v) => s + v, 0);
  const last = parseFloat((100 - sumExceptLast).toFixed(2));
  return [...rounded.slice(0, -1).map(v => v.toFixed(2)), last.toFixed(2)];
}

export const AssetAllocationChart: React.FC<AssetAllocationChartProps> = ({
  data,
  formatCurrency
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No asset allocation data available
      </div>
    );
  }

  const displayPcts = computeDisplayPercentages(data);

  return (
    <div className="flex flex-col lg:flex-row items-center gap-8">
      <div className="flex-1 flex justify-center">
        <div className="h-[300px] w-[390px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(props: any) => {
                  const { cx, cy, midAngle, outerRadius, name, index } = props;
                  if (!name || index === undefined) return null;
                  const pct = displayPcts[index];

                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 40;
                  let x = cx + radius * Math.cos(-midAngle * RADIAN);
                  let y = cy + radius * Math.sin(-midAngle * RADIAN);

                  // Ensure labels stay within visible bounds with more margin for text
                  const containerWidth = 360;
                  const containerHeight = 300;
                  const marginX = 40; // More horizontal margin for text
                  const marginY = 30; // Vertical margin

                  if (x < marginX) x = marginX;
                  if (x > containerWidth - marginX) x = containerWidth - marginX;
                  if (y < marginY) y = marginY;
                  if (y > containerHeight - marginY) y = containerHeight - marginY;

                  return (
                    <text
                      x={x}
                      y={y}
                      fill={COLORS[index % COLORS.length]}
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                      fontSize="13"
                      fontWeight="700"
                      stroke="white"
                      strokeWidth="4"
                      paintOrder="stroke fill"
                    >
                      {`${name} ${pct}%`}
                    </text>
                  );
                }}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: unknown) => {
                  if (typeof value === 'number') {
                    return formatCurrency(value);
                  }
                  return '-';
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex-1">
        <div className="space-y-4">
          {data.map((asset, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></div>
                <span className="font-medium text-gray-900 truncate">{asset.name}</span>
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                <div className="font-semibold text-gray-900">{formatCurrency(asset.value)}</div>
                <div className="text-sm text-gray-600">{displayPcts[index]}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
